/**
 * Labstep
 *
 * @module core/Form/Reusable
 * @desc Generic Form implementation to abstract all types of fields
 */

import { yupResolver } from '@hookform/resolvers/yup';
import { FormErrorMessage as Message } from 'labstep-web/core/Form/ReactForm/Errors/Message';
import { FormReusableActions } from 'labstep-web/core/Form/Reusable/Actions';
import { FormReusableFields } from 'labstep-web/core/Form/Reusable/Fields';
import React from 'react';
import { useForm } from 'react-hook-form';
import { Form } from 'semantic-ui-react';
import { type AnyObjectSchema, object } from 'yup';
import styles from './styles.module.scss';
import type {
  FieldWithoutActionProps,
  IFormReusableProps,
  IValues,
  preSubmitType,
} from './types';
import { flattenDefaultValues, getPreSubmit } from './utils';

export const FormReusable: React.FC<IFormReusableProps> = (props) => {
  const {
    fields,
    defaultValues,
    onSubmit,
    error,
    onFormChange,
    blurToggle,
    preSubmit: customPreSubmit,
    inline,
    status,
    autoFocus,
    cancelButton,
    submitButton,
    submitButtonText,
    color,
    onCancel,
    formRef,
  } = props;

  const schema: AnyObjectSchema = object(
    fields.reduce((acc, field) => {
      if (field.fieldType === 'action') {
        return acc;
      }
      return { ...acc, [field.name as string]: field.validation };
    }, {}),
  );

  const {
    handleSubmit,
    formState,
    getValues,
    setValue,
    control,
    reset,
    watch,
    register,
    unregister,
  } = useForm({
    defaultValues: flattenDefaultValues(defaultValues),
    resolver: yupResolver(schema),
  });

  const { errors } = formState;

  const defaultPreSubmit = getPreSubmit(
    fields as FieldWithoutActionProps[],
  );
  const preSubmit: preSubmitType = customPreSubmit
    ? (values): IValues => customPreSubmit(defaultPreSubmit(values))
    : (values): IValues => defaultPreSubmit(values);

  const watchAllFields = watch();

  React.useEffect(() => {
    const fieldNames = fields
      .filter((f) => f.fieldType !== 'action')
      .map((f) => (f as FieldWithoutActionProps).name);
    const registerredFieldNames = Object.keys(control._fields);

    //  Register fields that are not registered yet and unregister fields that are not in the form anymore
    fieldNames.forEach((fieldName) => {
      if (!registerredFieldNames.includes(fieldName)) {
        register(fieldName);
      }
    });
    registerredFieldNames.forEach((fieldName) => {
      if (!fieldNames.includes(fieldName)) {
        unregister(fieldName);
      }
    });
  }, [fields]);

  React.useEffect(() => {
    if (onFormChange) {
      onFormChange(preSubmit(watchAllFields));
    }
  }, [watchAllFields]);

  const onSubmitFinal = handleSubmit((values): void => {
    onSubmit(preSubmit(values), reset);
  });

  React.useImperativeHandle(formRef, () => ({
    submit: onSubmitFinal,
  }));

  const values = getValues();

  const fieldsComponent = (
    <FormReusableFields
      values={values}
      submitForm={onSubmitFinal}
      fields={fields}
      control={control}
      disabled={!!status?.isFetching}
      autoFocus={autoFocus}
      reset={reset}
      blurToggle={blurToggle}
      errors={errors}
    />
  );

  const errorMessage = !!error && <Message message={error} />;

  return (
    <Form
      data-cy="core/Form/Reusable"
      className={styles.form}
      onSubmit={onSubmitFinal}
    >
      {inline ? (
        <>
          <Form.Group inline>{fieldsComponent}</Form.Group>
          {errorMessage}
        </>
      ) : (
        <>
          {fieldsComponent}
          <div className={styles.message}>{errorMessage}</div>
          <FormReusableActions
            cancelButton={cancelButton}
            values={values}
            submitButton={submitButton}
            submitButtonText={submitButtonText}
            status={status}
            onCancel={onCancel}
            color={color}
            submitForm={onSubmitFinal}
            setValue={setValue}
          />
        </>
      )}
    </Form>
  );
};

FormReusable.defaultProps = {
  defaultValues: {},
};
